/**
 * The Pro brand mark, exported from Figma node `1:627` ("Union") in
 * `NC8kFoO9YUxSlCMzf5CopK`. The outline and the gradient stops are Figma's own
 * export, so the mark cannot drift from the design through a hand re-draw.
 *
 * Figma stacks three identical paths there - a flat `#5A2AFF` fill, the
 * gradient fill, then a 1px `#5A2AFF` stroke - and only the gradient one
 * survives here:
 *   - the flat base sits under an opaque fill of identical geometry, so it is
 *     provably never visible.
 *   - the stroke is an export artifact. Figma's canvas does not draw it: ink
 *     coverage measured against Figma's own PNG render of the node is 0.296,
 *     and this mark is 0.292 without the stroke but 0.420 with it - i.e. the
 *     exported stroke makes the gem ~42% heavier than the design.
 *
 * Also dropped: `preserveAspectRatio="none"`, which would stretch the mark,
 * and the fixed gradient id, which is namespaced per instance because SVG ids
 * are global.
 *
 * Shared by the native icon (react-native-svg's SvgXml) and its web twin
 * (inline markup), the same way `brand-logos.ts` feeds both platforms.
 */

/** The design's box. The mark is wider than it is tall - do not assume square. */
export const PRO_MARK_WIDTH = 24;
export const PRO_MARK_HEIGHT = 18;
export const PRO_MARK_ASPECT = PRO_MARK_WIDTH / PRO_MARK_HEIGHT;

/** Figma's `Union` outline, unmodified. */
export const PRO_MARK_PATH =
  'M17.6719 0.0107422H17.8535L17.9805 0.136719L23.6943 5.78223L24 6.08496L11.8506 18L0 6.37695L6.50195 0H6.68262L17.6719 0.0107422ZM12.1289 6.43555L7.70996 2.10156L3.49414 6.23633L11.9629 14.542L20.6191 6.05176L16.5693 2.08008L12.1289 6.43555Z';

/** The ramp Figma paints across the mark and the tier label alike. */
export const PRO_RAMP_FROM = '#8467FD';
export const PRO_RAMP_TO = '#5A2AFF';

/**
 * The framing the mark shipped in before this re-export: a 14 x 14 square with
 * the gem drawn 12.19 wide inside it, i.e. the gem spanned 87% of the box and
 * sat centred in it. Every caller outside the account hub was sized by eye
 * against that square, so `boxed` reproduces it and stays the default - a
 * re-export of the artwork must not resize the gem on screens nobody looked
 * at. Only the hub's Pro lockup asks for the mark's own 24 x 18 aspect.
 */
const LEGACY_BOX_MARK_FRACTION = 12.191511 / 14;
export const PRO_MARK_BOX_SIDE = PRO_MARK_WIDTH / LEGACY_BOX_MARK_FRACTION;

/** Centre the mark in the legacy square by growing the viewBox around it. */
const BOXED_VIEW_BOX = [
  (PRO_MARK_WIDTH - PRO_MARK_BOX_SIDE) / 2,
  (PRO_MARK_HEIGHT - PRO_MARK_BOX_SIDE) / 2,
  PRO_MARK_BOX_SIDE,
  PRO_MARK_BOX_SIDE,
]
  .map((value) => value.toFixed(4))
  .join(' ');

/**
 * Figma's export as markup. `id` namespaces the gradient; pass a `tint` to
 * flatten the ramp for callers that sit on a coloured surface (a violet
 * button, a photo swatch), where the design's own ramp would disappear.
 *
 * `boxed` pads the mark into the legacy square (see `PRO_MARK_BOX_SIDE`). The
 * gradient is unaffected either way: its coordinates are in the mark's own
 * user space, which the padded viewBox does not move.
 */
export function proMarkSvg({
  id,
  tint,
  boxed = false,
  width = boxed ? PRO_MARK_BOX_SIDE : PRO_MARK_WIDTH,
  height = boxed ? PRO_MARK_BOX_SIDE : PRO_MARK_HEIGHT,
}: {
  id: string;
  tint?: string;
  boxed?: boolean;
  width?: number;
  height?: number;
}) {
  const fill = tint ?? `url(#${id})`;
  const defs = tint
    ? ''
    : `<defs><linearGradient id="${id}" x1="0" y1="9" x2="24" y2="9" gradientUnits="userSpaceOnUse"><stop stop-color="${PRO_RAMP_FROM}"/><stop offset="1" stop-color="${PRO_RAMP_TO}"/></linearGradient></defs>`;
  const viewBox = boxed
    ? BOXED_VIEW_BOX
    : `0 0 ${PRO_MARK_WIDTH} ${PRO_MARK_HEIGHT}`;

  return `<svg width="${width}" height="${height}" viewBox="${viewBox}" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="Union"><path d="${PRO_MARK_PATH}" fill="${fill}"/></g>${defs}</svg>`;
}
